import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import dayjs from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";

type MarginPeriod = components["schemas"]["MarginPeriod"];

interface InternalCampaignWithMargins {
  id: string;
  name: string;
  margins: MarginPeriod[];
}

interface MarginTableProps {
  internalCampaigns: InternalCampaignWithMargins[];
  showViewButton?: boolean;
  hideInternalCampaignColumn?: boolean;
}

const MarginTable: React.FC<MarginTableProps> = ({
  internalCampaigns,
  showViewButton = true,
  hideInternalCampaignColumn = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleViewClick = (
    e: React.MouseEvent,
    internalCampaign: { id: string; name: string }
  ) => {
    e.stopPropagation();
    navigate(`/metrics/internal-metrics/${internalCampaign.id}`, {
      state: { internalCampaignName: internalCampaign.name },
    });
  };

  // Check if there are any margins across all internal campaigns
  const hasAnyMargins = internalCampaigns.some(
    (campaign) => campaign.margins && campaign.margins.length > 0
  );

  if (!hasAnyMargins) {
    return (
      <Box sx={{ mt: 2, p: 3, textAlign: "center" }}>
        <Typography variant="body1">
          {t(
            "metrics.marginTable.noMargins",
            "No margin periods defined for this campaign"
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table sx={{ minWidth: 650 }} aria-label="margins table">
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.background.default,
            }}
          >
            {!hideInternalCampaignColumn && (
              <TableCell>
                {t("campaigns.internalCampaign", "Internal Campaign")}
              </TableCell>
            )}
            <TableCell>
              {t("metrics.marginTable.startDate", "Start Date")}
            </TableCell>
            <TableCell>
              {t("metrics.marginTable.endDate", "End Date")}
            </TableCell>
            <TableCell align="right">
              {t("metrics.marginTable.margin", "Margin")} (%)
            </TableCell>
            {showViewButton && (
              <TableCell align="center">
                {t("common.actions", "Actions")}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {internalCampaigns.map((internalCampaign) =>
            internalCampaign.margins && internalCampaign.margins.length > 0
              ? internalCampaign.margins.map((marginPeriod, index) => (
                  <TableRow
                    key={`${internalCampaign.id}-${index}`}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    {!hideInternalCampaignColumn && (
                      <TableCell component="th" scope="row">
                        {internalCampaign.name}
                      </TableCell>
                    )}
                    <TableCell>
                      {dayjs(marginPeriod.startDate).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell>
                      {dayjs(marginPeriod.endDate).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell align="right">
                      {marginPeriod.margin.toFixed(2)}%
                    </TableCell>
                    {showViewButton && (
                      <TableCell align="center">
                        <IconButton
                          onClick={(e) =>
                            handleViewClick(e, {
                              id: internalCampaign.id,
                              name: internalCampaign.name,
                            })
                          }
                          size="small"
                          sx={{
                            color: theme.palette.info.main,
                            "&:hover": {
                              backgroundColor: theme.palette.info.light + "20",
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              : null
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MarginTable;
